import React from "react";
import {
  Box,
  Typography,
  IconButton,
  Divider,
  useTheme,
  Drawer,
  Tooltip,
} from "@mui/material";
import { Link } from "react-router-dom";
import ModalFooter from "@/modules/common/components/modals/modal-footer";
import {
  X,
  Calendar,
  User,
  Home,
  CopyCheck,
  Check,
  Settings,
} from "lucide-react";
import { muiTheme } from "@/styles/theme";

import { Task } from "@/modules/tasks/dtos";
import CommonAvatar from "@/modules/common/components/ui-kit/common-avatar";
import { nawyTheme } from "@/styles/nawy-theme/nawy-theme";
import InfoRow from "./info-row";
import {
  checkIfTaskIsSubmittedOverdue,
  formatSmartDate,
  checkIfTaskIsOverdue,
} from "@/modules/tasks/utils";
import { capitalizeFirstLetter } from "@/modules/tasks/utils/index";
import { formatDateReadable } from "@/modules/leads/utils";
import { mapTypeToDisplay } from "@/modules/tasks/utils";
import { useMeetingCompletion } from "@/modules/tasks/hooks/use-meeting-completion.hook";
import { MeetingCompletionModal } from "@/modules/tasks/components/meeting-completion-modal";

interface TaskDetailsSidebarProps {
  task: Task | null;
  isOpen: boolean;
  onClose: () => void;
  onMarkAsDone: (taskId: string) => void;
  isUpdating?: boolean;
}

const TaskDetailsSidebar: React.FC<TaskDetailsSidebarProps> = ({
  task,
  isOpen,
  onClose,
  onMarkAsDone,
}) => {
  const theme = useTheme();

  // Use the meeting completion hook
  const { state, handlers, utilities } = useMeetingCompletion({
    onSuccess: onClose,
  });

  if (!task) {
    return null;
  }

  const handleMarkAsDone = () => {
    // If it's a meeting task, open the evaluation modal
    if (utilities.isMeetingTask(task)) {
      handlers.openModalForTask(task);
      return;
    }

    // For non-meeting tasks, complete directly
    onMarkAsDone(task.uid || task.id.toString());
    onClose();
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={isOpen}
        onClose={onClose}
        sx={{
          zIndex: 1300,
          "& .MuiDrawer-paper": {
            backgroundColor: nawyTheme.nawy.colors.primitives.white[100],
            borderLeft: theme.nawy.elevation.sm,
            boxShadow: "-4px 0 8px rgba(0, 0, 0, 0.1)",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            zIndex: 1300,
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: `${theme.nawy.spacing.lg} ${theme.nawy.spacing.xl} ${theme.nawy.spacing.md} ${theme.nawy.spacing.xl}`,
            borderBottom: `1px solid ${theme.nawy.colors.primitives.navy[200]}`,
          }}
        >
          <Typography
            sx={{
              ...theme.nawy.typography.english.variants.headline.xs,
              color: nawyTheme.nawy.colors.semantic.text.primary,
            }}
          >
            Task details
          </Typography>
          <IconButton
            onClick={onClose}
            sx={{
              color: theme.nawy.colors.semantic.text.primary,
              "&:hover": {
                backgroundColor: theme.nawy.colors.primitives.navy[50],
              },
            }}
          >
            <X size={theme.nawy.spacing.lg} />
          </IconButton>
        </Box>
        <Divider />
        {/* Content */}
        <Box
          sx={{
            flex: 1,
            padding: `${theme.nawy.spacing.md} ${theme.nawy.spacing.xl} ${theme.nawy.spacing.xl} ${theme.nawy.spacing.xl}`,
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            gap: theme.nawy.spacing.xs,
            fontFamily: `${
              (muiTheme.components?.MuiCssBaseline?.styleOverrides as any)?.body
                ?.fontFamily
            }`,
          }}
        >
          {/* Task Title */}

          {task.status !== "completed" ? (
            <Typography
              sx={{
                ...theme.nawy.typography.english.variants.headline.xs,
                color: theme.nawy.colors.semantic.text.primary,
                width: "340px",
              }}
            >
              {capitalizeFirstLetter(task.title)}
            </Typography>
          ) : (
            <Typography
              sx={{
                ...theme.nawy.typography.english.variants.headline.xs,
                color: theme.nawy.colors.semantic.text.primary,
                width: "300px",
              }}
            >
              <IconButton
                sx={{
                  backgroundColor: theme.nawy.colors.primitives.blue[600],
                  borderRadius: "50%",
                  width: theme.nawy.spacing.lg,
                  height: theme.nawy.spacing.lg,
                  minWidth: theme.nawy.spacing.lg,
                  padding: 0,
                  marginRight: theme.nawy.spacing.sm,
                  marginBottom: theme.nawy.spacing.sm,
                  "&:hover": {
                    backgroundColor: theme.nawy.colors.primitives.blue[600],
                  },
                }}
                disabled={false}
              >
                <Check
                  size={theme.nawy.spacing.md}
                  color={theme.nawy.colors.primitives.white[100]}
                />
              </IconButton>
              {capitalizeFirstLetter(task.title)}
            </Typography>
          )}

          {/* Task Details */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: theme.nawy.spacing.lg,
            }}
          >
            {/* Task Type */}
            {task.type ? (
              <InfoRow
                icon={
                  <CopyCheck
                    size={theme.nawy.spacing.lg}
                    color={theme.nawy.colors.semantic.text.primary}
                  />
                }
                title="Task type"
                text={capitalizeFirstLetter(mapTypeToDisplay(task.type))}
              />
            ) : (
              <InfoRow
                icon={
                  <CopyCheck
                    size={theme.nawy.spacing.lg}
                    color={theme.nawy.colors.semantic.text.primary}
                  />
                }
                title="Task type"
                text="None"
              />
            )}

            {/* Linked Property ID */}
            <InfoRow
              icon={
                <Home
                  size={theme.nawy.spacing.lg}
                  color={theme.nawy.colors.semantic.text.primary}
                />
              }
              title={`Linked to ${
                task.linkedEntities?.[0]?.metaData?.type || ""
              }`}
            >
              {task.linkedEntities?.[0]?.metaData?.type === "property" ? (
                <Link
                  to={`/property-tracks/${task.linkedEntities[0]?.systemUId}`}
                  target="_blank"
                  style={{
                    fontSize:
                      theme.nawy.typography.english.variants.body.md.regular
                        .fontSize,
                    color: theme.nawy.colors.semantic.text.accent,
                    textDecoration: "underline",
                    cursor: "pointer",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = theme.nawy.colors.semantic
                      .text.primary as string;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = theme.nawy.colors.semantic
                      .text.accent as string;
                  }}
                >
                  {`#${task.linkedEntities?.[0]?.metaData?.id}`}
                </Link>
              ) : task.linkedEntities?.[0]?.metaData?.type === "lead" ? (
                <Link
                  to={`/leads-new/${task.linkedEntities?.[0]?.metaData.id}`}
                  target="_blank"
                  style={{
                    fontSize:
                      theme.nawy.typography.english.variants.body.md.regular
                        .fontSize,
                    color: theme.nawy.colors.semantic.text.accent,
                    textDecoration: "underline",
                    cursor: "pointer",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = theme.nawy.colors.semantic
                      .text.primary as string;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = theme.nawy.colors.semantic
                      .text.accent as string;
                  }}
                >
                  {`#${task.linkedEntities?.[0]?.metaData?.id}`}
                </Link>
              ) : (
                <Typography
                  sx={{
                    fontSize:
                      theme.nawy.typography.english.variants.body.md.regular
                        .fontSize,
                    color: theme.nawy.colors.semantic.text.secondary,
                  }}
                >
                  No linked entity
                </Typography>
              )}
            </InfoRow>

            {/* Assignee */}
            <InfoRow
              icon={
                <User
                  size={theme.nawy.spacing.lg}
                  color={theme.nawy.colors.semantic.text.primary}
                />
              }
              title="Assignee"
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: theme.nawy.spacing.sm,
                }}
              >
                <CommonAvatar
                  name={task.User?.name || "Unknown"}
                  color={
                    task.User?.color || theme.nawy.colors.semantic.text.tertiary
                  }
                  size={24}
                  fontSize={{
                    fontSize:
                      theme.nawy.typography.english.variants.body.sm.regular
                        .fontSize,
                  }}
                />
                <Typography
                  sx={{
                    fontSize:
                      theme.nawy.typography.english.variants.body.md.regular
                        .fontSize,
                    color: theme.nawy.colors.semantic.text.primary,
                  }}
                >
                  {task.User?.name || ""} (You)
                </Typography>
              </Box>
            </InfoRow>

            {/* Due Date */}

            <InfoRow
              icon={
                <Calendar
                  size={theme.nawy.spacing.lg}
                  color={theme.nawy.colors.semantic.text.primary}
                />
              }
              text={
                task.dueDate ? formatSmartDate(task.dueDate) : "No due date"
              }
              color={
                checkIfTaskIsSubmittedOverdue(
                  task.status,
                  task.dueDate,
                  task?.completedAt || ""
                )
                  ? nawyTheme.nawy.colors.semantic.text.extension.negative
                  : theme.nawy.colors.semantic.text.primary
              }
              title="Due date"
            />
            {/* completion  Date */}

            {task.completedAt && (
              <InfoRow
                icon={
                  <Calendar
                    size={theme.nawy.spacing.lg}
                    color={theme.nawy.colors.semantic.text.primary}
                  />
                }
                text={formatSmartDate(task.completedAt)}
                color={
                  checkIfTaskIsSubmittedOverdue(
                    task.status,
                    task.dueDate,
                    task?.completedAt || ""
                  )
                    ? nawyTheme.nawy.colors.semantic.text.extension.negative
                    : theme.nawy.colors.semantic.text.primary
                }
                title="Completion date"
              />
            )}

            {/* Creation Info */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: theme.nawy.spacing.xxs,
              }}
            >
              {!task.CreatedBy?.name ||
              task.CreatedBy?.name.toLowerCase() === "system" ? (
                <>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Settings size={20} />
                  </Box>
                  <Typography
                    sx={{
                      fontSize:
                        theme.nawy.typography.english.variants.body.md.regular
                          .fontSize,
                      color: theme.nawy.colors.semantic.text.secondary,
                    }}
                  >
                    System created task{" "}
                    {task.createdAt ? formatDateReadable(task.createdAt) : ""}
                  </Typography>
                </>
              ) : (
                <>
                  <CommonAvatar
                    name={task.CreatedBy?.name}
                    color={
                      task.CreatedBy?.color ||
                      theme.nawy.colors.semantic.layer.primary
                    }
                    size={32}
                    fontSize={{
                      fontSize:
                        theme.nawy.typography.english.variants.body.md.regular
                          .fontSize,
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize:
                        theme.nawy.typography.english.variants.body.md.regular
                          .fontSize,
                      color: theme.nawy.colors.semantic.text.secondary,
                    }}
                  >
                    {task.CreatedBy?.name} created task{" "}
                    {task.createdAt ? formatDateReadable(task.createdAt) : ""}
                  </Typography>
                </>
              )}
            </Box>
          </Box>
        </Box>

        {/* Footer Actions */}
        {task.status !== "completed" && (
          <Box
            sx={{
              padding: `${theme.nawy.spacing.lg} ${theme.nawy.spacing.xl}`,
              borderTop: `1px solid ${theme.nawy.colors.primitives.navy[200]}`,
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Tooltip
              title={
                utilities.isMeetingTask(task) &&
                checkIfTaskIsOverdue(task.status, task.dueDate)
                  ? "This task is past due, so it can't be marked as done"
                  : ""
              }
              disableHoverListener={
                !utilities.isMeetingTask(task) ||
                !checkIfTaskIsOverdue(task.status, task.dueDate)
              }
              disableFocusListener={
                !utilities.isMeetingTask(task) ||
                !checkIfTaskIsOverdue(task.status, task.dueDate)
              }
              disableTouchListener={
                !utilities.isMeetingTask(task) ||
                !checkIfTaskIsOverdue(task.status, task.dueDate)
              }
              arrow
            >
              <Box sx={{ width: "100%" }}>
                <ModalFooter
                  handleClose={onClose}
                  handleConfirm={handleMarkAsDone}
                  confirmText="Mark as done"
                  cancelText="Cancel"
                  isSubmitting={false}
                  disabledConfirmButton={
                    utilities.isMeetingTask(task) &&
                    checkIfTaskIsOverdue(task.status, task.dueDate)
                  }
                  showCancelButton={true}
                />
              </Box>
            </Tooltip>
          </Box>
        )}
      </Drawer>

      {/* Modal for Meeting Task Completion */}
      {utilities.isMeetingTask(task) && (
        <MeetingCompletionModal
          open={state.isModalOpen}
          onClose={handlers.handleCloseModal}
          onConfirm={handlers.handleConfirmModal}
          task={state.selectedTask}
          confirmButtonText={state.confirmButtonText}
          isLoading={state.isLoading}
          hasIncompleteTasks={state.hasIncompleteTasks}
          tasksReadyForEvaluation={state.tasksReadyForEvaluation}
          markAsCompletedRef={state.markAsCompletedRef}
          onTasksChange={handlers.setMeetingTasks}
          onValidationChange={handlers.handleValidationChange}
          onTasksNeedEvaluation={handlers.handleTasksNeedEvaluation}
          onTasksReadyForEvaluation={handlers.handleTasksReadyForEvaluation}
        />
      )}
    </>
  );
};

export default TaskDetailsSidebar;
